/**
 * Calcule le BMI à partir du poids et de la taille
 * @param {number} weight - Poids en kg
 * @param {number} height - Taille en mètres
 * @returns {number} BMI arrondi à 1 décimale
 */
export const calculateBMI = (weight, height) => {
  if (!weight || !height || height === 0) return 0;
  return parseFloat((weight / (height * height)).toFixed(1));
};

/**
 * Détermine la classification BMI
 * @param {number} bmi - Valeur du BMI
 * @returns {string} Classification
 */
export const getBMIClassification = (bmi) => {
  if (bmi < 18.5) return 'Underweight';
  if (bmi <= 24.9) return 'Normal weight';
  if (bmi <= 29.9) return 'Overweight';
  if (bmi <= 34.9) return 'Obesity Class I';
  if (bmi <= 39.9) return 'Obesity Class II';
  return 'Obesity Class III';
};

/**
 * Obtient la couleur selon la valeur BMI
 * @param {number} bmi - Valeur du BMI
 * @returns {string} Code couleur
 */
export const getBMIColor = (bmi) => {
  if (bmi < 18.5) return '#F6974A'; // Underweight - orange
  if (bmi <= 24.9) return '#5E9E4D'; // Normal - vert
  if (bmi <= 29.9) return '#F6974A'; // Overweight - orange
  if (bmi <= 34.9) return '#E46B6B'; // Obesity Class I - rouge
  return '#D92B2B'; // Obesity Class II+ - rouge foncé
};

/**
 * Génère des données BMI historiques (simulées pour l'exemple)
 * En production, ces données viendraient de l'API
 * @param {number} currentBMI - BMI actuel
 * @param {string} range - Période ('1M', '6M', '1Y', 'ALL')
 * @returns {Object} Données formatées pour le graphique
 */
export const generateBMIHistory = (currentBMI, range = 'ALL') => {
  const now = new Date();
  const data = {
    '1M': {
      labels: [],
      values: []
    },
    '6M': {
      labels: [],
      values: []
    },
    '1Y': {
      labels: [],
      values: []
    },
    'ALL': {
      labels: [],
      values: []
    }
  };

  // Générer des données pour chaque période
  const generateDataForPeriod = (months, pointsCount) => {
    const labels = [];
    const values = [];
    const variation = currentBMI * 0.1; // Variation de ±10%

    for (let i = pointsCount - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(date.getMonth() - (i * (months / pointsCount)));
      
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      labels.push(monthName);

      // Générer une valeur qui tend vers le BMI actuel
      const progress = 1 - (i / pointsCount);
      const randomFactor = (Math.random() - 0.5) * variation;
      const value = currentBMI + randomFactor * (1 - progress);
      values.push(parseFloat(value.toFixed(1)));
    }

    // S'assurer que la dernière valeur est le BMI actuel
    values[values.length - 1] = currentBMI;

    return { labels, values };
  };

  // 1 mois - 4 points (chaque semaine)
  data['1M'] = generateDataForPeriod(1, 4);

  // 6 mois - 6 points (chaque mois)
  data['6M'] = generateDataForPeriod(6, 6);

  // 1 an - 12 points (chaque mois)
  data['1Y'] = generateDataForPeriod(12, 12);

  // ALL - 24 points (2 ans)
  data['ALL'] = generateDataForPeriod(24, 24);

  return data;
};

/**
 * Convertit la taille de différents formats vers mètres
 * @param {string|number} height - Taille (peut être "1.65 Cm", "165", 1.65, etc.)
 * @returns {number} Taille en mètres
 */
export const parseHeight = (height) => {
  if (!height) return 0;
  
  // Si c'est déjà un nombre
  if (typeof height === 'number') {
    // Si > 3, on considère que c'est en cm
    return height > 3 ? height / 100 : height;
  }

  // Si c'est une string, extraire le nombre
  const numStr = height.toString().replace(/[^0-9.]/g, '');
  const num = parseFloat(numStr);
  
  if (isNaN(num)) return 0;
  
  // Si > 3, on considère que c'est en cm
  return num > 3 ? num / 100 : num;
};

/**
 * Convertit le poids de différents formats vers kg
 * @param {string|number} weight - Poids (peut être "56 Kg", "56", 56, etc.)
 * @returns {number} Poids en kg
 */
export const parseWeight = (weight) => {
  if (!weight) return 0;
  
  // Si c'est déjà un nombre
  if (typeof weight === 'number') return weight;

  // Si c'est une string, extraire le nombre
  const numStr = weight.toString().replace(/[^0-9.]/g, '');
  const num = parseFloat(numStr);
  
  return isNaN(num) ? 0 : num;
};